function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Container, CssContainer } from "../../components/container";
import { PageHeader, Card } from "../../components/analytics-kit";
import { Link } from "react-router-dom";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import account from "../../data/account";
import { hasTenantFeature, ownAdAccounts } from "../../functions/tenant-features";
import { adSvc } from "./common";

/*
 * Ad Provider Settings (§98, mockup ad-provider-settings-2026-09-12).
 * OWNER/ADMIN only — account wiring ids, not everyday controls; usually a
 * one-time onboarding act done with CS on a call. Two provider cards
 * (Google / Meta) + the Rung-2 "Share conversion signals" consent switch,
 * which points at the Meta card when the pixel is missing.
 *
 * Contracts (FlightController, live both stages):
 *  GET /v1/campaign/ad/provider-settings → [{provider, clientId, accountId,
 *      pageId, instagramId, pixelId, modifiedDate}]
 *  PUT /v1/campaign/ad/provider-settings  {provider: Google|Facebook, ...fields}
 *  GET/PUT /v1/campaign/ad/event-forwarding {enabled} — the consent moment,
 *      audit-logged server-side.
 *
 * This route is the deep-link target for the flight wizard's
 * "provider settings required" validation errors.
 */

const isAdmin = () => {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root"].some(g => groups.includes(g));
};
const GOOGLE_FIELDS = [["clientId", "Customer ID", "xxx-xxx-xxxx"]];
const META_FIELDS = [["accountId", "Ad account ID", ""], ["pageId", "Page ID", ""], ["instagramId", "Instagram ID (optional)", ""], ["pixelId", "Pixel / dataset ID", ""]];
// Managed / self-service: Plunk is the provider (its own Google client + Meta ad
// account). The tenant owns only its Page, Instagram and pixel.
const META_FIELDS_MANAGED = [["pageId", "Facebook Page ID", "e.g. 123456789012345"], ["instagramId", "Instagram account ID (optional — filled in automatically once access is verified)", ""], ["pixelId", "Pixel / dataset ID (optional)", ""]];
class ProviderSettingsView extends React.Component {
  constructor(props) {
    super(props);
    // Own ad accounts (self_hosted / Shopify Commerce, 2026-09-26): Google manager-link
    // status + request; Meta ad-account partner-access check. Both read the SAVED ids.
    _defineProperty(this, "loadGoogleLink", async () => {
      if (!ownAdAccounts() || !(this.savedRow("google") || {}).clientId) return;
      try {
        const s = await adSvc("/provider-settings/google/link").getAsync();
        this.setState({
          googleLink: s
        });
      } catch (e) {
        this.setState({
          googleLink: {
            status: "unknown"
          }
        });
      }
    });
    _defineProperty(this, "requestGoogleLink", async () => {
      this.setState({
        linking: true,
        note: null
      });
      try {
        const s = await adSvc("/provider-settings/google/link").postAsync({});
        this.setState({
          linking: false,
          googleLink: s,
          note: "Link request sent — accept it in Google Ads (Admin › Access and security › Managers)."
        });
      } catch (e) {
        let msg = "Couldn't send the link request — try again in a minute.";
        try {
          msg = JSON.parse(e.message).error || msg;
        } catch (x) {/* plain */}
        this.setState({
          linking: false,
          note: msg
        });
      }
    });
    _defineProperty(this, "verifyAccountAccess", async () => {
      this.setState({
        verifyingAcct: true
      });
      try {
        const a = await adSvc("/meta-account/verify").getAsync();
        this.setState({
          acctAccess: a,
          verifyingAcct: false
        });
      } catch (e) {
        let msg = "Couldn't verify — try again in a minute.";
        try {
          msg = JSON.parse(e.message).error || msg;
        } catch (x) {/* plain */}
        this.setState({
          verifyingAcct: false,
          acctAccess: {
            granted: false,
            error: msg
          }
        });
      }
    });
    // Managed mode: does the Page grant Plunk's Business Manager advertiser access?
    _defineProperty(this, "verifyAccess", async silent => {
      this.setState({
        verifying: true,
        note: null
      });
      try {
        const a = await adSvc("/meta-pages/verify").getAsync();
        this.setState({
          access: a,
          verifying: false
        });
        // a user-initiated verify that just granted access reloads the row (Instagram may have been filled)
        if (!silent && a && a.granted) this.load();
      } catch (e) {
        let msg = "Couldn't verify — try again in a minute.";
        try {
          msg = JSON.parse(e.message).error || msg;
        } catch (x) {/* plain */}
        this.setState({
          verifying: false,
          access: {
            granted: false,
            error: msg
          }
        });
      }
    });
    _defineProperty(this, "save", async providerKey => {
      const isGoogle = providerKey === "google";
      // Overwrite guard: values were already set — campaigns created after
      // the change use the new account; existing campaigns are unaffected.
      if (this.hadValues(providerKey === "google" ? "google" : "facebook") && this.state.confirm !== providerKey) {
        this.setState({
          confirm: providerKey
        });
        return;
      }
      const body = isGoogle ? {
        provider: "Google",
        clientId: (this.state.google.clientId || "").trim()
      } : this.state.managedByPlunk ? {
        provider: "Facebook",
        pageId: (this.state.meta.pageId || "").trim(),
        instagramId: (this.state.meta.instagramId || "").trim(),
        pixelId: (this.state.meta.pixelId || "").trim()
      } : {
        provider: "Facebook",
        accountId: (this.state.meta.accountId || "").trim(),
        pageId: (this.state.meta.pageId || "").trim(),
        instagramId: (this.state.meta.instagramId || "").trim(),
        pixelId: (this.state.meta.pixelId || "").trim()
      };
      this.setState({
        busy: providerKey,
        confirm: null,
        note: null
      });
      try {
        await adSvc("/provider-settings").putAsync(body);
        this.setState({
          busy: null,
          note: `${isGoogle ? "Google Ads" : "Meta"} settings saved.`,
          dirty: {
            ...this.state.dirty,
            [providerKey]: false
          }
        });
        this.load();
      } catch (e) {
        this.setState({
          busy: null,
          note: "Save failed — check the values and try again."
        });
      }
    });
    _defineProperty(this, "toggleForwarding", async () => {
      const next = !this.state.forwarding;
      this.setState({
        fwBusy: true,
        note: null
      });
      try {
        await adSvc("/event-forwarding").putAsync({
          enabled: next
        });
        this.setState({
          forwarding: next,
          fwBusy: false
        });
      } catch (e) {
        this.setState({
          fwBusy: false,
          note: "Couldn't update the setting — try again."
        });
      }
    });
    this.state = {
      rows: null,
      error: false,
      google: {},
      meta: {},
      // editable field values
      dirty: {},
      // {Google: bool, Facebook: bool}
      confirm: null,
      // provider awaiting overwrite confirm
      busy: null,
      // provider being saved
      note: null,
      forwarding: null,
      fwBusy: false,
      business: null,
      // {id, name} — Plunk's Business Manager (managed mode)
      access: null,
      verifying: false // meta-pages/verify result
    };
  }
  componentDidMount() {
    this._alive = true;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  load() {
    adSvc("/provider-settings").getAsync().then(d => {
      // §215 crash fix: the endpoint grew an envelope —
      // {managedByPlunk, settings: []} — where the §98 contract was a
      // bare array. `.forEach` on the object threw, the catch set
      // error, and the page rendered "Something went wrong" despite
      // both calls returning 200. Accept both shapes.
      const rows = Array.isArray(d) ? d : d && (d.settings || d.items) || [];
      const managedByPlunk = !Array.isArray(d) && !!(d && d.managedByPlunk);
      const business = !Array.isArray(d) && d && d.business || null;
      const byP = {};
      rows.forEach(r => {
        byP[String(r.provider || "").toLowerCase()] = r;
      });
      // Managed mode: the chip must reflect Meta's actual answer, not the last
      // click in this browser — re-check access on every load when a Page is set.
      const fbRow = byP.facebook || {};
      if (managedByPlunk && fbRow.pageId && !this.state.verifying) setTimeout(() => this._alive !== false && this.verifyAccess(true), 0);
      this.setState({
        rows,
        managedByPlunk,
        business,
        google: {
          clientId: (byP.google || {}).clientId || ""
        },
        meta: {
          accountId: (byP.facebook || {}).accountId || "",
          pageId: (byP.facebook || {}).pageId || "",
          instagramId: (byP.facebook || {}).instagramId || "",
          pixelId: (byP.facebook || {}).pixelId || ""
        }
      });
    }).catch(() => this.setState({
      error: true
    }));
    adSvc("/event-forwarding").getAsync().then(d => this.setState({
      forwarding: !!(d && d.enabled)
    })).catch(() => this.setState({
      forwarding: null
    }));
  }
  savedRow(provider) {
    return (this.state.rows || []).find(r => String(r.provider || "").toLowerCase() === provider) || null;
  }
  hadValues(provider) {
    const r = this.savedRow(provider);
    return !!(r && (r.clientId || r.accountId || r.pageId || r.pixelId));
  }
  componentDidUpdate(prev, prevState) {
    if (!prevState.rows && this.state.rows) this.loadGoogleLink();
  }
  renderOwnGoogle() {
    const r = this.savedRow("google") || {};
    const l = this.state.googleLink || {};
    const st = String(l.status || "").toUpperCase();
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        borderTop: "1px solid var(--plk-border, #e4e7ec)",
        paddingTop: 10
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px",
        maxWidth: "70ch"
      }
    }, "Campaigns run on ", /*#__PURE__*/React.createElement("b", null, "your"), " Google Ads account \u2014 you pay Google directly. Plunk manages it through a manager-account link: save your Customer ID, send the link request, then accept it in Google Ads (", /*#__PURE__*/React.createElement("b", null, "Admin \u203A Access and security \u203A Managers"), ")."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: this.state.linking || !r.clientId || st === "ACTIVE",
      title: !r.clientId ? "Save the Customer ID first" : undefined,
      onClick: this.requestGoogleLink
    }, this.state.linking ? "Sending…" : st === "PENDING" ? "Resend link request" : "Send link request"), st === "ACTIVE" && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5
      }
    }, "\u2713 Linked \u2014 Plunk can manage campaigns on ", r.clientId, "."), st === "PENDING" && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5,
        color: "var(--plk-accent, #ff6358)"
      }
    }, "Request pending \u2014 accept it in Google Ads, then reload."), (st === "NONE" || st === "UNKNOWN") && r.clientId && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5
      }
    }, "Not linked yet.")));
  }
  renderOwnMeta() {
    const r = this.savedRow("facebook") || {};
    const a = this.state.acctAccess;
    const bizId = (this.state.business || {}).id || "";
    const bizName = (this.state.business || {}).name || "Plunk";
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 10,
        borderTop: "1px solid var(--plk-border, #e4e7ec)",
        paddingTop: 10
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px",
        maxWidth: "70ch"
      }
    }, "Campaigns run on ", /*#__PURE__*/React.createElement("b", null, "your"), " Meta ad account \u2014 you pay Meta directly. Grant ", bizName, " partner access once (a Business admin does this):"), /*#__PURE__*/React.createElement("ol", {
      style: {
        fontSize: 12.5,
        margin: "0 0 10px 18px",
        padding: 0,
        lineHeight: 1.7
      }
    }, /*#__PURE__*/React.createElement("li", null, "Open ", /*#__PURE__*/React.createElement("b", null, "Meta Business Suite \u2192 Settings \u2192 Accounts \u2192 Ad accounts"), " and select the account."), /*#__PURE__*/React.createElement("li", null, "Click ", /*#__PURE__*/React.createElement("b", null, "Assign partner"), " \u2192 ", /*#__PURE__*/React.createElement("b", null, "Business ID"), " and enter ", /*#__PURE__*/React.createElement("code", {
      style: {
        fontFamily: "monospace"
      }
    }, bizId || "…"), " (", bizName, "); turn on ", /*#__PURE__*/React.createElement("b", null, "Manage campaigns"), "."), /*#__PURE__*/React.createElement("li", null, "Do the same for your ", /*#__PURE__*/React.createElement("b", null, "Page"), " (Create ads) and, if you use it, your ", /*#__PURE__*/React.createElement("b", null, "Pixel"), "."), /*#__PURE__*/React.createElement("li", null, "Save the ad account and Page IDs above, then click ", /*#__PURE__*/React.createElement("b", null, "Verify access"), ".")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: this.state.verifyingAcct || !r.accountId,
      title: !r.accountId ? "Save the ad account ID first" : undefined,
      onClick: this.verifyAccountAccess
    }, this.state.verifyingAcct ? "Checking…" : "Verify access"), a && a.granted && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5
      }
    }, "\u2713 ", /*#__PURE__*/React.createElement("b", null, a.name || r.accountId), " grants ", bizName, " access", a.relation === "partner" ? " (partner)" : "", "."), a && !a.granted && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5,
        color: "var(--plk-accent, #ff6358)"
      }
    }, a.error || "Not granted yet — check the partner assignment and try again.")));
  }
  chip(provider) {
    if (provider === "google") {
      const v = (this.savedRow("google") || {}).clientId;
      return v ? ["CONNECTED", "ok"] : ["NOT CONNECTED", "off"];
    }
    const r = this.savedRow("facebook") || {};
    if (this.state.managedByPlunk) {
      if (!r.pageId) return ["NOT CONNECTED", "off"];
      if (this.state.access && this.state.access.granted) return ["CONNECTED", "ok"];
      if (!this.state.access || this.state.verifying) return ["CHECKING ACCESS…", "off"];
      return ["ACCESS NOT GRANTED", "warn"];
    }
    if (r.accountId && r.pageId && r.pixelId) return ["CONNECTED", "ok"];
    if (r.accountId && r.pageId) return ["PIXEL MISSING", "warn"];
    return ["NOT CONNECTED", "off"];
  }
  field(stateKey, provider, [key, label, ph]) {
    return /*#__PURE__*/React.createElement("label", {
      key: key,
      style: {
        display: "block",
        fontSize: 12,
        marginBottom: 10
      }
    }, label, /*#__PURE__*/React.createElement("input", {
      value: this.state[stateKey][key] || "",
      placeholder: ph,
      onChange: e => this.setState({
        [stateKey]: {
          ...this.state[stateKey],
          [key]: e.target.value
        },
        dirty: {
          ...this.state.dirty,
          [provider]: true
        },
        confirm: null
      }),
      style: {
        width: "100%",
        boxSizing: "border-box",
        marginTop: 4,
        padding: "7px 9px",
        fontSize: 13,
        fontFamily: "monospace",
        border: "1px solid var(--plk-border, #d5dbe3)",
        borderRadius: 6
      }
    }));
  }

  // Managed mode: the grant-advertiser step (Meta Business Settings → Pages →
  // Assign partner → Plunk's Business ID → "Create ads"), then Verify.
  renderGrantAccess() {
    const {
      business,
      access,
      verifying
    } = this.state;
    const r = this.savedRow("facebook") || {};
    const bizId = (business || {}).id || "";
    const bizName = (business || {}).name || "Plunk";
    return /*#__PURE__*/React.createElement(Card, {
      title: "Grant Plunk advertiser access"
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 8px",
        maxWidth: "70ch"
      }
    }, "Meta only lets Plunk run ads for your Page after your Page grants our business the ", /*#__PURE__*/React.createElement("b", null, "Create ads"), " task. One-time, done by a Page admin:"), /*#__PURE__*/React.createElement("ol", {
      style: {
        fontSize: 12.5,
        margin: "0 0 10px 18px",
        padding: 0,
        lineHeight: 1.7
      }
    }, /*#__PURE__*/React.createElement("li", null, "Open ", /*#__PURE__*/React.createElement("b", null, "Meta Business Suite \u2192 Settings \u2192 Accounts \u2192 Pages"), " and select your Page."), /*#__PURE__*/React.createElement("li", null, "Click ", /*#__PURE__*/React.createElement("b", null, "Assign partner"), " \u2192 ", /*#__PURE__*/React.createElement("b", null, "Business ID"), " and enter ", /*#__PURE__*/React.createElement("code", {
      style: {
        fontFamily: "monospace"
      }
    }, bizId || "…"), " (", bizName, ")."), /*#__PURE__*/React.createElement("li", null, "Turn on ", /*#__PURE__*/React.createElement("b", null, "Create ads"), " (advertise) and ", /*#__PURE__*/React.createElement("b", null, "View performance"), " (analyze), then save."), /*#__PURE__*/React.createElement("li", null, "Come back here and click ", /*#__PURE__*/React.createElement("b", null, "Verify access"), ".")), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: verifying || !r.pageId,
      title: !r.pageId ? "Save the Page ID first" : undefined,
      onClick: this.verifyAccess
    }, verifying ? "Checking…" : "Verify access"), access && access.granted && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5
      }
    }, "\u2713 ", /*#__PURE__*/React.createElement("b", null, access.pageName || r.pageId), " grants ", bizName, " advertiser access", access.relation === "owned" ? " (owned page)" : "", access.instagramId ? ` · Instagram ${access.instagramId}` : "", "."), access && !access.granted && /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5,
        color: "var(--plk-accent, #ff6358)"
      }
    }, access.error ? access.error : `Not granted yet — ${access.pageName ? `"${access.pageName}"` : "this Page"} hasn't assigned ${bizName} the Create ads task. Finish the steps above, then verify again.`)));
  }
  chipEl([label, cls]) {
    const colors = {
      ok: ["#0d2415", "#86e0a1"],
      warn: ["#3a2b08", "#ffd246"],
      off: ["#eef0f3", "#8a94a6"]
    };
    const dark = cls !== "off";
    return /*#__PURE__*/React.createElement("span", {
      className: "simf-pill",
      style: {
        background: colors[cls][0],
        color: colors[cls][1],
        border: dark ? "none" : undefined
      }
    }, label);
  }
  render() {
    if (!hasTenantFeature("campaigns")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Campaigns isn't configured for this workspace."));
    }
    if (!isAdmin()) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Ad provider settings are available to workspace owners and administrators."));
    }
    const {
      rows,
      error,
      confirm,
      busy,
      note,
      forwarding,
      fwBusy,
      dirty
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (rows === null) return /*#__PURE__*/React.createElement(Loader, null);
    const metaChip = this.chip("facebook");
    const pixelMissing = metaChip[0] === "PIXEL MISSING" || !(this.savedRow("facebook") || {}).pixelId;
    const card = (title, providerKey, chipVal, fields, stateKey, hint, emptyLine) => /*#__PURE__*/React.createElement(Card, {
      title: null
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 10
      }
    }, /*#__PURE__*/React.createElement("b", {
      style: {
        fontSize: 15
      }
    }, title), this.chipEl(chipVal)), !this.hadValues(providerKey === "google" ? "google" : "facebook") && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 10px"
      }
    }, emptyLine), fields.map(f => this.field(stateKey, providerKey, f)), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "2px 0 10px"
      }
    }, hint), confirm === providerKey ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: 12.5
      }
    }, "Campaigns created after this change will use the new account \u2014 existing campaigns are unaffected."), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => this.save(providerKey)
    }, "Confirm save"), /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: () => this.setState({
        confirm: null
      })
    }, "Cancel")) : /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy === providerKey || !dirty[providerKey],
      onClick: () => this.save(providerKey)
    }, busy === providerKey ? "Saving…" : "Save"));
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view campaigns-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Campaigns"
      }, "Campaigns"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Campaigns/Flights"
      }, "Flights"), " \xB7 Settings"),
      title: "Ad provider settings",
      subtitle: "Account wiring for campaign launch and audience sync. Usually a one-time onboarding step: each card walks through where the ids live."
    }), note && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, note), this.state.managedByPlunk && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, "Plunk runs your campaigns on its own Google and Meta ad accounts, so there is nothing to wire there. For Social channels, connect your Facebook Page (and Instagram) below and grant Plunk advertiser access."), /*#__PURE__*/React.createElement("div", {
      className: "territory-cards",
      style: {
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        alignItems: "flex-start"
      }
    }, this.state.managedByPlunk ? /*#__PURE__*/React.createElement("div", {
      style: {
        flex: "1 1 340px",
        maxWidth: 560
      }
    }, card("Meta (Facebook & Instagram)", "facebook", metaChip, META_FIELDS_MANAGED, "meta", "Page ID: Facebook Page → About → Page transparency. Pixel id from Meta Events Manager → Data sources (needed only for conversion sharing).", "Social and Social Video campaigns run under your Facebook Page. Enter the Page ID, save, then grant access below."), this.renderGrantAccess()) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: "1 1 340px",
        maxWidth: 480
      }
    }, card("Google Ads", "google", this.chip("google"), GOOGLE_FIELDS, "google", "From Google Ads → the top-right account badge (xxx-xxx-xxxx). Powers campaign creation and the audience catalog's Google side.", ownAdAccounts() ? "Enter your Google Ads Customer ID, save, then send the manager link request below." : "Connect your Google Ads account to launch campaigns from Plunk. Your account manager can help."), ownAdAccounts() && this.renderOwnGoogle()), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: "1 1 340px",
        maxWidth: 480
      }
    }, card("Meta (Facebook & Instagram)", "facebook", metaChip, META_FIELDS, "meta", "Pixel id from Meta Events Manager → Data sources. Without it, conversion sharing can be ON but nothing sends.", ownAdAccounts() ? "Enter your Meta ad account and Page IDs, save, then grant Plunk partner access below." : "Connect your Meta account to launch campaigns from Plunk. Your account manager can help."), ownAdAccounts() && this.renderOwnMeta()))), /*#__PURE__*/React.createElement(Card, {
      title: "Share conversion signals with ad platforms"
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap"
      }
    }, /*#__PURE__*/React.createElement("label", {
      style: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13.5,
        lineHeight: 1,
        cursor: forwarding === null ? "default" : "pointer"
      }
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!forwarding,
      disabled: fwBusy || forwarding === null,
      style: {
        margin: 0,
        width: 16,
        height: 16,
        flex: "0 0 auto"
      },
      onChange: this.toggleForwarding
    }), /*#__PURE__*/React.createElement("span", null, forwarding === null ? "Unavailable right now" : forwarding ? "On" : "Off")), /*#__PURE__*/React.createElement("span", {
      className: "territory-hint",
      style: {
        margin: 0
      }
    }, "Sends purchase and lead events back to the ad exchanges so their bidding learns from real outcomes. Turning this on is the consent action and is recorded in the Activity Log.")), forwarding && pixelMissing && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        borderLeft: "3px solid #ffd246",
        paddingLeft: 10,
        marginTop: 10
      }
    }, "Conversion sharing is on, but the Meta pixel id is missing \u2014 nothing sends to Meta until it's set in the card above.")));
  }
}
const Routable = withRouter(ProviderSettingsView);
export default class ProviderSettingsViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}